<template>
    <div class="mp-install-page">
        <div class="mp-consent-wrapper">

            <!-- Loading -->
            <div v-if="loading" class="mp-consent-card mp-consent-card--loading">
                <b-spinner variant="primary" />
                <p class="mp-consent-loading-text">{{ $t('marketplace.install.loading') }}</p>
            </div>

            <!-- Error / Not found -->
            <div v-else-if="error" class="mp-consent-card">
                <div class="mp-consent-error-icon">
                    <i class="bi bi-exclamation-triangle-fill"></i>
                </div>
                <h3 class="mp-consent-heading">{{ $t('marketplace.install.notFound') }}</h3>
                <p class="mp-consent-sub">{{ error }}</p>
                <button class="mp-consent-btn mp-consent-btn--secondary mt-4" @click="cancel">
                    {{ $t('cancel') }}
                </button>
            </div>

            <!-- Consent window -->
            <div v-else-if="item" class="mp-consent-card">

                <!-- Requesting app badge -->
                <div class="mp-consent-origin" v-if="appName">
                    <i class="bi bi-grid-3x3-gap-fill mr-1"></i>
                    <span>{{ appName }}</span>
                    <i class="bi bi-arrow-right mx-2 mp-consent-arrow"></i>
                    <span class="mp-consent-origin-target">{{ $t('marketplace.title') }}</span>
                </div>

                <!-- Product identity -->
                <div class="mp-consent-product">
                    <div class="mp-consent-product-icon">
                        <img
                            v-if="itemIcon && (itemIcon.startsWith('http') || itemIcon.startsWith('data:'))"
                            :src="itemIcon"
                            :alt="itemName"
                        />
                        <img
                            v-else-if="itemIcon && itemIcon.startsWith('image:')"
                            :src="itemIcon.replace('image:', '')"
                            :alt="itemName"
                        />
                        <i
                            v-else-if="itemIcon && itemIcon.startsWith('b-icon:')"
                            :class="'bi bi-' + itemIcon.replace('b-icon:', '')"
                        ></i>
                        <i v-else-if="type === 'integration'" class="bi bi-puzzle-fill"></i>
                        <i v-else-if="type === 'template'" class="bi bi-file-earmark-text-fill"></i>
                        <i v-else class="bi bi-grid-3x3-gap-fill"></i>
                    </div>
                    <div class="mp-consent-product-info">
                        <h2 class="mp-consent-product-name">{{ itemName }}</h2>
                        <span
                            class="mp-consent-type-badge"
                            :class="'mp-consent-type-badge--' + type"
                        >
                            {{ $t('marketplace.' + type) }}
                        </span>
                    </div>
                </div>

                <!-- Description -->
                <p v-if="itemDescription" class="mp-consent-description">
                    {{ itemDescription }}
                </p>

                <!-- Required integrations -->
                <div v-if="requiredIntegrations.length > 0" class="mp-consent-includes">
                    <div class="mp-consent-includes-label">{{ $t('marketplace.install.includes') }}</div>
                    <div
                        v-for="dep in requiredIntegrations"
                        :key="dep.id"
                        class="mp-consent-includes-item"
                    >
                        <i class="bi bi-puzzle-fill mr-2"></i>
                        <span>{{ $translateLabel(dep.label) || dep.id }}</span>
                    </div>
                </div>

                <!-- Inactive integration notice -->
                <b-alert v-if="isIntegrationInactive" variant="warning" show class="mt-3 mb-0">
                    <i class="bi bi-pause-circle-fill mr-2"></i>
                    {{ $t('marketplace.install.integrationInactive') }}
                </b-alert>

                <!-- Price -->
                <div v-if="!isFree && (priceLoading || itemPrice)" class="mp-consent-price">
                    <span class="mp-consent-price-label">{{ $t('marketplace.install.price') }}</span>
                    <span v-if="priceLoading" class="mp-consent-price-value">
                        <b-spinner small variant="primary" />
                    </span>
                    <span v-else class="mp-consent-price-value">{{ itemPrice }}</span>
                </div>

                <hr class="mp-consent-divider" />

                <!-- Action block -->
                <div class="mp-consent-action-block">
                    <div
                        class="mp-consent-action-icon"
                        :class="{
                            'mp-consent-action-icon--activate': canActivateDirectly,
                            'mp-consent-action-icon--checkout': !canActivateDirectly
                        }"
                    >
                        <i v-if="canActivateDirectly" class="bi bi-lightning-charge-fill"></i>
                        <i v-else class="bi bi-cart3"></i>
                    </div>
                    <div class="mp-consent-action-text">
                        <div class="mp-consent-action-title">
                            {{ canActivateDirectly
                                ? $t('marketplace.install.activateTitle')
                                : $t('marketplace.install.purchaseTitle') }}
                        </div>
                        <div class="mp-consent-action-desc">
                            {{ canActivateDirectly
                                ? $t('marketplace.install.activateDesc', { name: itemName })
                                : $t('marketplace.install.purchaseDesc', { name: itemName }) }}
                        </div>
                    </div>
                </div>

                <!-- Already installed notice -->
                <b-alert v-if="alreadyDone" variant="success" show class="mt-3 mb-0">
                    <i class="bi bi-check-circle-fill mr-2"></i>
                    {{ $t('marketplace.install.alreadyActive') }}
                </b-alert>

                <!-- Success state -->
                <div v-if="done && !alreadyDone" class="mp-consent-success">
                    <i class="bi bi-check-circle-fill"></i>
                    <span>{{ $t('marketplace.install.done') }}</span>
                </div>

                <!-- Error from action -->
                <b-alert v-if="actionError" variant="danger" show class="mt-3 mb-0">
                    <i class="bi bi-exclamation-circle-fill mr-2"></i>
                    {{ actionError }}
                </b-alert>

                <!-- Checkout pending notice -->
                <b-alert v-if="checkoutOpened" variant="info" show class="mt-3 mb-0">
                    <i class="bi bi-info-circle-fill mr-2"></i>
                    {{ $t('marketplace.install.checkoutOpened') }}
                </b-alert>

                <!-- Footer -->
                <div class="mp-consent-footer">
                    <button
                        v-if="!done && !alreadyDone"
                        class="mp-consent-btn mp-consent-btn--secondary"
                        @click="cancel"
                        :disabled="acting"
                    >
                        {{ $t('cancel') }}
                    </button>

                    <button
                        v-if="!done && !alreadyDone && !checkoutOpened"
                        class="mp-consent-btn"
                        :class="canActivateDirectly ? 'mp-consent-btn--primary' : 'mp-consent-btn--checkout'"
                        @click="confirm"
                        :disabled="acting"
                    >
                        <b-spinner v-if="acting" small class="mr-2" />
                        <i v-else-if="canActivateDirectly" class="bi bi-lightning-charge-fill mr-2"></i>
                        <i v-else class="bi bi-cart3 mr-2"></i>
                        {{ canActivateDirectly
                            ? $t('marketplace.activate')
                            : $t('marketplace.install.goToCheckout') }}
                    </button>

                    <button
                        v-if="done || alreadyDone || checkoutOpened"
                        class="mp-consent-btn mp-consent-btn--primary mp-consent-btn--full"
                        @click="returnToApp"
                    >
                        <i class="bi bi-arrow-left mr-2"></i>
                        {{ $t('marketplace.install.backToApp') }}
                    </button>
                </div>

            </div>

        </div>
    </div>
</template>

<script>
import Price from '../../plugins/protokol/lib/Price'

export default {
    name: 'MarketplaceInstall',

    data() {
        return {
            loading: true,
            error: null,
            item: null,
            subscriptions: [],
            installedTemplates: [],
            activeIntegrations: [],
            installedApps: [],
            acting: false,
            done: false,
            alreadyDone: false,
            actionError: null,
            checkoutOpened: false,
            priceHelper: null,
            priceData: null,
            priceLoading: false,
        }
    },

    computed: {
        type() {
            return this.$route.query.type || 'integration'
        },
        itemId() {
            return this.$route.query.id || ''
        },
        returnUrl() {
            return this.$route.query.return || null
        },
        appName() {
            return this.$route.query.initiator || null
        },

        itemName() {
            if (!this.item) return ''
            return this.$translateLabel(this.item.label || this.item.name || '')
        },
        itemIcon() {
            if (!this.item) return null
            return this.item.icon || this.item.cover || null
        },
        itemDescription() {
            if (!this.item) return null
            const desc = this.item.short_description || this.item.description
            if (!desc) return null
            return this.$translateLabel(desc)
        },

        isFree() {
            if (!this.item) return false
            // If price was fetched and is zero, treat as free regardless of item fields
            if (!this.priceLoading && this.priceData && this.priceData.total_amount !== undefined) {
                return parseFloat(this.priceData.total_amount) === 0
            }
            if (this.type === 'integration') {
                return this.item.free === true || this.item.price === 0 || this.item.price === null || this.item.price === undefined
            }
            if (this.type === 'app') {
                const set = this.item.option_set
                if (!set || !set.items || !set.items.length) return true
                const def = set.items.find(function(o) { return o.default }) || set.items[0]
                return !def.price || def.price === 0
            }
            return false
        },

        isOwned() {
            if (!this.item) return false
            const id = this.item.id || this.item.uuid || this.item.product_id
            const productId = this.item.product_id || id
            return this.subscriptions.some(function(s) {
                if (s.product_id !== id && s.product_id !== productId) return false
                if (s.payment_provider === 'activation_code' && s.expires_at) {
                    var now = new Date()
                    var expires = new Date(s.expires_at)
                    var daysLeft = (expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
                    if (daysLeft <= 14) return false
                }
                return true
            })
        },

        isRenewal() {
            if (!this.item) return false
            const id = this.item.id || this.item.uuid || this.item.product_id
            const productId = this.item.product_id || id
            return this.subscriptions.some(function(s) {
                if (s.product_id !== id && s.product_id !== productId) return false
                if (s.payment_provider !== 'activation_code' || !s.expires_at) return false
                var now = new Date()
                var expires = new Date(s.expires_at)
                var daysLeft = (expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
                return daysLeft <= 14
            })
        },

        isAlreadyActive() {
            if (!this.item) return false
            if (this.type === 'integration') {
                const id = this.item.id
                return this.activeIntegrations.some(function(i) {
                    return i.id === id && i.status === 'active'
                })
            }
            if (this.type === 'app') {
                const uuid = this.item.uuid || this.item.id
                return this.installedApps.some(function(a) {
                    return a.uuid === uuid || a.external_id === uuid
                })
            }
            if (this.type === 'template') {
                const id = this.item.id
                return this.installedTemplates.some(function(t) {
                    return t.id === id || t.template_id === id
                })
            }
            return false
        },

        isIntegrationInactive() {
            if (!this.item || this.type !== 'integration') return false
            const id = this.item.id
            return this.activeIntegrations.some(function(i) {
                return i.id === id && i.status !== 'active'
            })
        },

        requiredIntegrations() {
            if (!this.item || this.type !== 'app') return []
            var settings = this.item.settings
            if (!settings || !settings.length) return []
            var latest = settings[settings.length - 1]
            if (!latest.requires || !latest.requires.integrations) return []
            var integrations = this.activeIntegrations || []
            return latest.requires.integrations.filter(function(dep) {
                return !dep.optional
            }).map(function(dep) {
                var match = integrations.find(function(i) { return i.id === dep.id })
                return {
                    id: dep.id,
                    label: match ? (match.label || match.name || dep.id) : dep.id
                }
            })
        },

        canActivateDirectly() {
            if (this.isRenewal) return false
            return this.isFree || this.isOwned || this.isIntegrationInactive
        },

        itemCurrency() {
            return this.priceHelper ? this.priceHelper.getCurrency() : 'USD'
        },

        itemPrice() {
            if (!this.priceData || this.priceData.total_amount === undefined) return null
            const amount = parseFloat(this.priceData.total_amount).toFixed(2)
            const currency = this.itemCurrency
            if (currency === 'USD') return '$' + amount
            if (currency === 'RSD') return amount + ' RSD'
            return amount + ' ' + currency
        },
    },

    async mounted() {
        // Wait for user (with billing_address) to be available — same logic as List.vue
        let user = this.$store.getters.getUser

        if (!user || !user.settings || !user.settings.billing_address) {
            await new Promise(resolve => {
                const checkUser = setInterval(() => {
                    user = this.$store.getters.getUser
                    if (user && user.settings && user.settings.billing_address) {
                        clearInterval(checkUser)
                        resolve()
                    }
                }, 50)

                setTimeout(() => {
                    clearInterval(checkUser)
                    resolve()
                }, 3000)
            })
        }

        // Init price helper using user billing country
        user = this.$store.getters.getUser
        const billingAddress = user && user.settings && user.settings.billing_address
        const country = billingAddress ? billingAddress.country : 'en_US'
        this.priceHelper = new Price(country, this.$store.getters.getLocale)

        await this.loadData()
    },

    methods: {
        async loadData() {
            this.loading = true
            this.error = null
            try {
                const [subsResp] = await Promise.all([
                    this.$store.dispatch('fetchSubscriptions'),
                ])
                this.subscriptions = this.$store.getters.getSubscriptions || []

                // Load active integrations for already-active check
                try {
                    await this.$store.dispatch('fetchIntegrations')
                    this.activeIntegrations = this.$store.getters.getIntegrations || []
                } catch (e) {
                    this.activeIntegrations = []
                }

                // Load installed apps
                try {
                    const appsResp = await this.$http.get('/luma/appservice/v1/forge/marketplace', { params: { search: '' } })
                    this.installedApps = (appsResp.data || []).filter(function(a) { return a.installed })
                } catch (e) {
                    this.installedApps = []
                }

                // Load installed templates
                try {
                    const tmplResp = await this.$http.get('/v1/project/template/workspace')
                    this.installedTemplates = tmplResp.data || []
                } catch (e) {
                    this.installedTemplates = []
                }

                // Fetch the specific item
                await this.fetchItem()

                // Fetch price (non-blocking)
                this.fetchPrice()

                // Check if already done (but not if this is a renewal)
                if (this.item && this.isAlreadyActive && !this.isRenewal) {
                    this.alreadyDone = true
                }
            } catch (err) {
                this.error = (err.response && err.response.data && err.response.data.message)
                    || this.$t('marketplace.install.loadError')
            } finally {
                this.loading = false
            }
        },

        async fetchItem() {
            if (!this.itemId) {
                this.error = this.$t('marketplace.install.missingParams')
                return
            }

            if (this.type === 'integration') {
                const integrations = await this.$store.dispatch('fetchIntegrationsExplore')
                const found = (integrations || []).find(function(i) { return i.id === this.itemId }.bind(this))
                if (!found) {
                    this.error = this.$t('marketplace.install.notFound')
                    return
                }
                this.item = found

            } else if (this.type === 'app') {
                const resp = await this.$http.get('/luma/appservice/v1/forge/marketplace/' + this.itemId)
                this.item = resp.data

            } else if (this.type === 'template') {
                const resp = await this.$http.get('/v1/project/template/available')
                const all = resp.data || []
                const found = all.find(function(t) { return t.id === this.itemId }.bind(this))
                if (!found) {
                    this.error = this.$t('marketplace.install.notFound')
                    return
                }
                this.item = found
            } else {
                this.error = this.$t('marketplace.install.unknownType')
            }
        },

        async confirm() {
            if (this.acting) return
            this.acting = true
            this.actionError = null

            try {
                if (this.canActivateDirectly) {
                    await this.activate()
                } else {
                    await this.goToCheckout()
                }
            } catch (err) {
                this.actionError = (err.response && err.response.data && err.response.data.message)
                    || this.$t('error')
            } finally {
                this.acting = false
            }
        },

        async activate() {
            if (this.type === 'integration') {
                await this.$integrationApi.post('/integration/' + this.item.id + '/activate')
                await this.$store.dispatch('fetchIntegrations', true)
                this.done = true

            } else if (this.type === 'app') {
                await this.$http.post('/luma/appservice/v1/forge/install', {
                    external_id: this.item.uuid || this.item.id,
                })
                await this.$store.dispatch('fetchAllForgeApps')
                this.done = true

            } else if (this.type === 'template') {
                const popup = this.$loadingPopup(this.$t('marketplace.installingTemplate'))
                try {
                    const templateId = this.item.id
                    await this.$http.post('/v1/project/template/' + templateId + '/install')
                    this.done = true
                } finally {
                    popup.hide()
                }
            }
        },

        async fetchPrice() {
            if (!this.item || this.isFree) return
            this.priceLoading = true
            try {
                const productType = this.type
                const reference = this.type === 'app'
                    ? (this.item.uuid || this.item.id)
                    : (this.item.product_id || this.item.id || this.item.uuid)
                const resp = await this.$transactions.getPricesForProduct(productType, reference, this.itemCurrency)
                this.priceData = resp.data
            } catch (e) {
                // Non-critical — price display is best-effort
            } finally {
                this.priceLoading = false
            }
        },

        async goToCheckout() {
            const productType = this.type
            const reference = this.type === 'app'
                ? (this.item.uuid || this.item.id)
                : (this.item.product_id || this.item.id || this.item.uuid)

            const resp = await this.$transactionApi.post('/checkout/link', {
                product_type: productType,
                reference: reference,
                lang: this.$store.getters.getLocale,
                source: 'marketplace',
                currency: this.itemCurrency,
            })
            window.open(resp.data, '_blank')
            this.checkoutOpened = true
        },

        cancel() {
            if (this.returnUrl) {
                window.location.href = this.returnUrl
                return
            }
            if (window.parent !== window) {
                window.parent.postMessage({ type: 'marketplace_install_denied' }, '*')
                return
            }
            this.$router.push({ name: 'marketplace.List' })
        },

        returnToApp() {
            if (this.returnUrl) {
                window.location.href = this.returnUrl
                return
            }
            if (window.parent !== window) {
                window.parent.postMessage({
                    type: 'marketplace_install_done',
                    item_id: this.itemId,
                    item_type: this.type,
                    already_installed: this.alreadyDone,
                }, '*')
                return
            }
            this.$router.push({ name: 'marketplace.List' })
        },
    },
}
</script>

<style scoped>
.mp-install-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-page, #0f1117);
    padding: 2rem 1rem;
}

.mp-consent-wrapper {
    width: 100%;
    max-width: 480px;
}

/* Card */
.mp-consent-card {
    background: var(--bg-card, #1a1d27);
    border: 1px solid var(--border-default, rgba(255,255,255,0.08));
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 32px 80px rgba(0, 0, 0, 0.6);
    color: var(--text-primary, #e8eaf0);
}

.mp-consent-card--loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    gap: 1rem;
}

.mp-consent-loading-text {
    margin: 0;
    color: var(--text-secondary, #8a90a2);
    font-size: 0.9rem;
}

/* Error state */
.mp-consent-error-icon {
    font-size: 2.5rem;
    color: #e55353;
    text-align: center;
    margin-bottom: 1rem;
}

/* Requesting app origin */
.mp-consent-origin {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    color: var(--text-secondary, #8a90a2);
    margin-bottom: 1.75rem;
    padding: 0.5rem 0.875rem;
    background: var(--bg-card-secondary, rgba(255,255,255,0.04));
    border-radius: 20px;
    border: 1px solid var(--border-default, rgba(255,255,255,0.06));
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
}

.mp-consent-arrow {
    opacity: 0.5;
}

.mp-consent-origin-target {
    color: var(--text-primary, #e8eaf0);
    font-weight: 500;
}

/* Product identity */
.mp-consent-product {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
}

.mp-consent-product-icon {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    background: var(--bg-card-secondary, rgba(255,255,255,0.06));
    border: 1px solid var(--border-default, rgba(255,255,255,0.08));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.75rem;
    color: var(--primary, #5b73e8);
    flex-shrink: 0;
    overflow: hidden;
}

.mp-consent-product-icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 10px;
}

.mp-consent-product-info {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.mp-consent-product-name {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
    color: var(--text-primary, #e8eaf0);
    line-height: 1.2;
}

.mp-consent-type-badge {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 20px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    width: fit-content;
}

.mp-consent-type-badge--integration {
    background: rgba(91, 115, 232, 0.15);
    color: #7d97f4;
    border: 1px solid rgba(91, 115, 232, 0.25);
}

.mp-consent-type-badge--template {
    background: rgba(50, 200, 180, 0.12);
    color: #38c9b2;
    border: 1px solid rgba(50, 200, 180, 0.2);
}

.mp-consent-type-badge--app {
    background: rgba(255, 152, 48, 0.12);
    color: #ff9f43;
    border: 1px solid rgba(255, 152, 48, 0.2);
}

/* Description */
.mp-consent-description {
    font-size: 0.875rem;
    color: var(--text-secondary, #8a90a2);
    line-height: 1.6;
    margin: 0.5rem 0 0;
}

/* Price */
.mp-consent-price {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: var(--bg-card-secondary, rgba(255,255,255,0.03));
    border-radius: 8px;
    border: 1px solid var(--border-default, rgba(255,255,255,0.06));
    margin-top: 1rem;
}

.mp-consent-price-label {
    font-size: 0.8rem;
    color: var(--text-secondary, #8a90a2);
    font-weight: 500;
}

.mp-consent-price-value {
    font-size: 1rem;
    font-weight: 700;
    color: var(--text-primary, #e8eaf0);
}

/* Included products */
.mp-consent-includes {
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    background: var(--bg-card-secondary, rgba(255,255,255,0.03));
    border-radius: 8px;
    border: 1px solid var(--border-default, rgba(255,255,255,0.06));
}

.mp-consent-includes-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-secondary, #8a90a2);
    margin-bottom: 0.5rem;
}

.mp-consent-includes-item {
    display: flex;
    align-items: center;
    font-size: 0.85rem;
    color: var(--text-primary, #e8eaf0);
    padding: 0.3rem 0;
}

.mp-consent-includes-item i {
    color: var(--primary, #5b73e8);
    font-size: 0.8rem;
}

/* Divider */
.mp-consent-divider {
    border-color: var(--border-default, rgba(255,255,255,0.07));
    margin: 1.5rem 0;
}

/* Action block */
.mp-consent-action-block {
    display: flex;
    align-items: flex-start;
    gap: 0.875rem;
    padding: 1rem;
    background: var(--bg-card-secondary, rgba(255,255,255,0.03));
    border-radius: 10px;
    border: 1px solid var(--border-default, rgba(255,255,255,0.06));
}

.mp-consent-action-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
}

.mp-consent-action-icon--activate {
    background: rgba(91, 115, 232, 0.15);
    color: #7d97f4;
}

.mp-consent-action-icon--checkout {
    background: rgba(255, 152, 48, 0.12);
    color: #ff9f43;
}

.mp-consent-action-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary, #e8eaf0);
    margin-bottom: 0.2rem;
}

.mp-consent-action-desc {
    font-size: 0.8rem;
    color: var(--text-secondary, #8a90a2);
    line-height: 1.5;
}

/* Success */
.mp-consent-success {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-top: 1.25rem;
    padding: 0.875rem 1rem;
    background: rgba(46, 213, 115, 0.08);
    border: 1px solid rgba(46, 213, 115, 0.2);
    border-radius: 10px;
    color: #2ed573;
    font-size: 0.875rem;
    font-weight: 500;
}

.mp-consent-success i {
    font-size: 1.1rem;
}

/* Heading / sub for error */
.mp-consent-heading {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary, #e8eaf0);
    text-align: center;
    margin: 0.5rem 0;
}

.mp-consent-sub {
    font-size: 0.875rem;
    color: var(--text-secondary, #8a90a2);
    text-align: center;
    margin-bottom: 0.5rem;
}

/* Footer */
.mp-consent-footer {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.5rem;
    justify-content: flex-end;
}

/* Buttons */
.mp-consent-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: all 0.18s ease;
    min-height: 40px;
    white-space: nowrap;
}

.mp-consent-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.mp-consent-btn--secondary {
    background: transparent;
    border: 1px solid var(--border-hover, rgba(255,255,255,0.15));
    color: var(--text-secondary, #8a90a2);
}

.mp-consent-btn--secondary:hover:not(:disabled) {
    background: var(--bg-card-secondary, rgba(255,255,255,0.05));
    color: var(--text-primary, #e8eaf0);
}

.mp-consent-btn--primary {
    background: var(--primary, #5b73e8);
    color: #fff;
    box-shadow: 0 2px 12px rgba(91, 115, 232, 0.35);
}

.mp-consent-btn--primary:hover:not(:disabled) {
    opacity: 0.88;
    transform: translateY(-1px);
    box-shadow: 0 4px 18px rgba(91, 115, 232, 0.45);
}

.mp-consent-btn--checkout {
    background: #ff9f43;
    color: #fff;
    box-shadow: 0 2px 12px rgba(255, 152, 48, 0.3);
}

.mp-consent-btn--checkout:hover:not(:disabled) {
    opacity: 0.88;
    transform: translateY(-1px);
    box-shadow: 0 4px 18px rgba(255, 152, 48, 0.4);
}

.mp-consent-btn--full {
    width: 100%;
}
</style>
